import type { InvokeResult, ReduxSagaModel } from './types';
import InsightMemory from '@/services/insightMemory';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { handleSystemError, handleUserError } from '@/utils/handleError';

const ns = 'app.models.insightMemory';

// What the insights agent remembers about the current user in the current workspace (BEEM-1448),
// behind the "What Insights remembers" page.
export type State = {
  // False when the agent has no Memory configured in this environment; null until loaded.
  enabled: boolean | null;
  memories: InsightMemory[];
  nextToken?: string;
  invokeListResult: InvokeResult<null> | null;
  invokeSaveResult: InvokeResult<null> | null;
  // Set once "forget everything" is accepted: the agent erases in the background, so the page
  // says so rather than showing an empty list that may still refill for a moment.
  forgetAllRequested: boolean;
};

const initialState: State = {
  enabled: null,
  memories: [],
  nextToken: undefined,
  invokeListResult: null,
  invokeSaveResult: null,
  forgetAllRequested: false,
};

export type SaveMemoriesAction = {
  type: 'saveMemories' | 'insightMemory/saveMemories';
  payload: Partial<Pick<State, 'enabled' | 'memories' | 'nextToken' | 'forgetAllRequested'>>;
};

export type SaveInvokeListResultAction = {
  type: 'saveInvokeListResult' | 'insightMemory/saveInvokeListResult';
  payload: Pick<State, 'invokeListResult'>;
};

export type SaveInvokeSaveResultAction = {
  type: 'saveInvokeSaveResult' | 'insightMemory/saveInvokeSaveResult';
  payload: Pick<State, 'invokeSaveResult'>;
};

export type ListEffect = { type: 'insightMemory/list'; payload: { more?: boolean } };
export type CreateEffect = { type: 'insightMemory/create'; payload: { text: string } };
export type UpdateEffect = {
  type: 'insightMemory/update';
  payload: { memory: InsightMemory; text: string };
};
export type DeleteEffect = { type: 'insightMemory/delete'; payload: { memory: InsightMemory } };
export type ForgetAllEffect = { type: 'insightMemory/forgetAll'; payload: Record<string, never> };
export type ForgetConversationEffect = {
  type: 'insightMemory/forgetConversation';
  payload: { sessionId: string };
};

const InsightMemoryModel: ReduxSagaModel<
  State,
  {
    saveMemories: SaveMemoriesAction;
    saveInvokeListResult: SaveInvokeListResultAction;
    saveInvokeSaveResult: SaveInvokeSaveResultAction;
  }
> = {
  namespace: 'insightMemory',
  state: initialState,
  effects: {
    *list({ payload: { more } }: { payload: ListEffect['payload'] }, { put }) {
      try {
        const { memories, nextToken } = yield* sgselect((s) => s.insightMemory);
        const page = yield* sgcall(() => InsightMemory.list(more ? nextToken : undefined));
        yield put<SaveMemoriesAction>({
          type: 'saveMemories',
          payload: {
            enabled: page.enabled,
            memories: more ? memories.concat(page.memories) : page.memories,
            nextToken: page.nextToken,
            ...(more ? {} : { forgetAllRequested: false }),
          },
        });
        yield put<SaveInvokeListResultAction>({
          type: 'saveInvokeListResult',
          payload: { invokeListResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeListResultAction>({
          type: 'saveInvokeListResult',
          payload: {
            invokeListResult: { success: false, error: handleUserError(e, `${ns}.list.error`) },
          },
        });
      }
    },
    *create({ payload: { text } }: { payload: CreateEffect['payload'] }, { put }) {
      try {
        const created = yield* sgcall(() => InsightMemory.create(text));
        const { memories } = yield* sgselect((s) => s.insightMemory);
        if (created)
          yield put<SaveMemoriesAction>({
            type: 'saveMemories',
            payload: { memories: [created, ...memories] },
          });
        yield put<SaveInvokeSaveResultAction>({
          type: 'saveInvokeSaveResult',
          payload: { invokeSaveResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeSaveResultAction>({
          type: 'saveInvokeSaveResult',
          payload: {
            invokeSaveResult: { success: false, error: handleUserError(e, `${ns}.create.error`) },
          },
        });
      }
    },
    *update({ payload: { memory, text } }: { payload: UpdateEffect['payload'] }, { put }) {
      try {
        const updated = yield* sgcall(() => memory.update(text));
        const { memories } = yield* sgselect((s) => s.insightMemory);
        if (updated)
          yield put<SaveMemoriesAction>({
            type: 'saveMemories',
            payload: { memories: memories.map((m) => (m.id === memory.id ? updated : m)) },
          });
        yield put<SaveInvokeSaveResultAction>({
          type: 'saveInvokeSaveResult',
          payload: { invokeSaveResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeSaveResultAction>({
          type: 'saveInvokeSaveResult',
          payload: {
            invokeSaveResult: { success: false, error: handleUserError(e, `${ns}.update.error`) },
          },
        });
      }
    },
    *delete({ payload: { memory } }: { payload: DeleteEffect['payload'] }, { put }) {
      try {
        yield* sgcall(() => memory.delete());
        const { memories } = yield* sgselect((s) => s.insightMemory);
        yield put<SaveMemoriesAction>({
          type: 'saveMemories',
          payload: { memories: memories.filter((m) => m.id !== memory.id) },
        });
      } catch (e) {
        handleUserError(e, `${ns}.delete.error`);
      }
    },
    *forgetAll(_, { put }) {
      try {
        yield* sgcall(() => InsightMemory.forgetAll());
        yield put<SaveMemoriesAction>({
          type: 'saveMemories',
          payload: { memories: [], nextToken: undefined, forgetAllRequested: true },
        });
      } catch (e) {
        handleUserError(e, `${ns}.forgetAll.error`);
      }
    },
    // A deleted conversation must also leave the agent's memory. Best-effort and silent: the
    // conversation is already gone from the app, and there is nothing for the user to retry.
    *forgetConversation({
      payload: { sessionId },
    }: {
      payload: ForgetConversationEffect['payload'];
    }) {
      try {
        yield* sgcall(() => InsightMemory.forgetConversation(sessionId));
      } catch (e) {
        handleSystemError(e, `${ns}.forgetConversation`);
      }
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveMemories(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeListResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeSaveResult(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default InsightMemoryModel;
